/**
 * App-Zustand: liegt immer vollständig auf dem iPhone (IndexedDB) und wird
 * mit dem Server abgeglichen. Abhaken funktioniert sofort – auch offline.
 * Änderungen ohne Netz landen in einer Warteschlange und werden nachgeliefert.
 */
import { useSyncExternalStore } from 'react';
import { get, set } from 'idb-keyval';
import {
  AppState,
  CareEvent,
  type EventInput,
  type RegisterResponse,
  type Room,
  type SyncOp,
} from '@pflanzen/shared';
import { ApiError, api, path, setToken, getToken } from './api';

const STATE_KEY = 'pflanzen-state';
const QUEUE_KEY = 'pflanzen-queue';

export interface Store {
  state: AppState | null;
  /** Noch nicht beim Server angekommene Änderungen. */
  pending: SyncOp[];
  online: boolean;
  syncing: boolean;
  lastSyncAt: string | null;
  error: string | null;
  ready: boolean;
}

let store: Store = { state: null, pending: [], online: navigator.onLine, syncing: false, lastSyncAt: null, error: null, ready: false };
const listeners = new Set<() => void>();

function emit(patch: Partial<Store>): void {
  store = { ...store, ...patch };
  for (const l of listeners) l();
}

export function useStore(): Store {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => store,
  );
}

export function getStore(): Store {
  return store;
}

async function saveLocal(): Promise<void> {
  await set(STATE_KEY, store.state);
  await set(QUEUE_KEY, store.pending);
}

/** Beim Start: sofort den gespeicherten Stand zeigen, dann im Hintergrund abgleichen. */
export async function boot(): Promise<void> {
  const [raw, queue] = await Promise.all([get<unknown>(STATE_KEY), get<SyncOp[]>(QUEUE_KEY)]);
  const parsed = raw ? AppState.safeParse(raw) : null;
  emit({ state: parsed?.success ? parsed.data : null, pending: queue ?? [], ready: true });
  window.addEventListener('online', () => {
    emit({ online: true });
    void refresh();
  });
  window.addEventListener('offline', () => emit({ online: false }));
  if (await getToken()) void refresh();
}

/** Warteschlange senden, dann den vollständigen Stand vom Server holen. */
export async function refresh(): Promise<void> {
  if (store.syncing || !(await getToken())) return;
  emit({ syncing: true, error: null });
  try {
    if (store.pending.length) {
      const ops = store.pending;
      await api('POST', path('sync'), { ops });
      emit({ pending: store.pending.slice(ops.length) });
    }
    const fresh = AppState.parse(await api<unknown>('GET', path('state')));
    emit({ state: applyPending(fresh, store.pending), online: true, lastSyncAt: new Date().toISOString() });
    await saveLocal();
  } catch (e) {
    if (e instanceof ApiError && e.offline) emit({ online: false });
    else if (e instanceof ApiError && e.status === 401) emit({ error: 'Dieses Gerät ist nicht mehr angemeldet.' });
    else emit({ error: e instanceof Error ? e.message : String(e) });
  } finally {
    emit({ syncing: false });
  }
}

/** Wendet noch offene Änderungen lokal an (optimistisch). */
function applyPending(state: AppState, ops: SyncOp[]): AppState {
  let s = state;
  for (const op of ops) s = applyOp(s, op);
  return s;
}

function applyOp(s: AppState, op: SyncOp): AppState {
  if (op.op === 'event.add') {
    if (s.events.some((e) => e.id === op.data.id)) return s;
    const task = s.tasks.find((t) => t.id === op.data.taskId);
    if (!task) return s;
    const ev = CareEvent.parse({
      ...op.data,
      plantId: task.plantId,
      typeKey: task.typeKey,
      at: op.data.at ?? new Date().toISOString(),
    });
    return { ...s, events: [...s.events, ev] };
  }
  if (op.op === 'event.delete') {
    return { ...s, events: s.events.filter((e) => e.id !== op.id) };
  }
  if (op.op === 'room.state') {
    const now = new Date().toISOString();
    const rooms = s.rooms.map((r): Room => {
      if (r.id !== op.id) return r;
      const next = { ...r };
      if (op.window && op.window !== r.window) Object.assign(next, { window: op.window, windowChangedAt: now });
      if (op.curtain && op.curtain !== r.curtain) Object.assign(next, { curtain: op.curtain, curtainChangedAt: now });
      return next;
    });
    return { ...s, rooms };
  }
  return s;
}

/** Änderung sofort lokal anwenden, speichern und an den Server schicken. */
export async function enqueue(op: SyncOp): Promise<void> {
  const pending = [...store.pending, op];
  emit({ pending, state: store.state ? applyOp(store.state, op) : store.state });
  await saveLocal();
  void refresh();
}

export function newId(prefix: string): string {
  const r = crypto.getRandomValues(new Uint8Array(9));
  return `${prefix}_${Date.now().toString(36)}${[...r].map((b) => b.toString(36)).join('').slice(0, 10)}`;
}

// ── Bequeme Aktionen für die Oberfläche ─────────────────────────────

export async function logEvent(input: Omit<EventInput, 'id' | 'source' | 'note' | 'photoId' | 'snoozeUntil' | 'soil'> & Partial<EventInput>): Promise<string> {
  const id = input.id ?? newId('ev');
  const data: EventInput = {
    id,
    taskId: input.taskId,
    outcome: input.outcome,
    soil: input.soil ?? null,
    at: input.at ?? new Date().toISOString(),
    source: input.source ?? 'app',
    note: input.note ?? '',
    photoId: input.photoId ?? null,
    snoozeUntil: input.snoozeUntil ?? null,
  };
  await enqueue({ op: 'event.add', data });
  return id;
}

export async function undoEvent(id: string): Promise<void> {
  await enqueue({ op: 'event.delete', id });
}

export async function setRoomState(id: string, patch: { window?: Room['window']; curtain?: Room['curtain'] }): Promise<void> {
  await enqueue({ op: 'room.state', id, ...patch });
}

/** Direkte Server-Änderung (Pflanzen, Einstellungen …) – braucht Netz. */
export async function mutate<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await api<T>(method, url, body);
  await refresh();
  return res;
}

// ── Anmeldung ─────────────────────────────────────────────────────────

export async function registerDevice(label: string): Promise<RegisterResponse> {
  const res = await api<RegisterResponse>('POST', path('deviceRegister'), { label });
  await setToken(res.deviceToken);
  await refresh();
  return res;
}

export async function recoverDevice(recoveryCode: string, label: string): Promise<RegisterResponse> {
  const res = await api<RegisterResponse>('POST', path('deviceRecover'), { recoveryCode, label });
  await setToken(res.deviceToken);
  emit({ state: null, pending: [] });
  await refresh();
  return res;
}
